/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import {
    object, func, number, shape, string,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { useUIDSeed } from 'react-uid';

const styles = ({ palette, breakpoints }) => ({
    cyoImageWrapper: {
        width: '86px',
        height: '94px',
        position: 'relative',
    },
    cyoImageWrapperSelectedItems: {
        backgroundColor: palette.cms?.cyoSelectedImageLayer || '#33383d',
        zIndex: '1',
    },
    cyoImage: {
        width: '100%',
        height: '100%',
    },
    cyoImageSelectedItems: {
        opacity: '0.4',
    },
    prodName: {
        height: '50px',
        fontSize: '16px',
        marginLeft: '10px',
    },
    addRemoveIcon: {
        width: '45px',
        height: '45px',
        color: palette.cms?.cyoMobileBtnColor || '#607767',
        border: `2px solid ${(palette.cms?.cyoMobileBtnColor || '#607767')}`,
        borderRadius: '50px',
        [breakpoints.down(366)]: {
            width: '40px',
            height: '40px',
        },
        [breakpoints.down(340)]: {
            width: '35px',
            height: '35px',
        },
    },
    totalProds: {
        width: '36px',
        textAlign: 'center',
        fontSize: '20px',
        marginTop: '9px',
        [breakpoints.down(340)]: {
            marginTop: '3px',
        },
    },
    errorMessage: {
        color: palette.common.red,
        fontSize: '14px',
        padding: '2px',
    },
    removebtn: {
        borderRadius: '40px',
        padding: '0 20px 0 20px',
        marginLeft: '10px',
        border: `2px solid ${(palette.cms?.cyoMobileBtnColor || '#607767')}`,
        color: palette.cms?.cyoMobileBtnColor || '#607767',
        fontWeight: '800',
        height: '45px',
        [breakpoints.down(366)]: {
            height: '40px',
            padding: '0 15px 0 15px',
        },
        [breakpoints.down(340)]: {
            height: '35px',
        },
    },
    addedText: {
        position: 'absolute',
        top: '32px',
        left: '20px',
        color: palette.white || '#fff',
        fontWeight: '900',
        zIndex: '100',
        [breakpoints.down(340)]: {
            top: '35px',
            left: '16px',
            fontSize: '15px',
        },
    },
});

const MobileCYOItems = ({
    classes,
    maxComponents,
    cyoGroup,
    selectedItems,
    component,
    handleSelectedCyoProduct,
    errorMessage,
    handleRemoveProduct,
    selectedData,
    selectedBand,
}) => {
    const seed = useUIDSeed();
    if (Object.keys(component).length === 0) {
        return null;
    }
    const maxSelectables = maxComponents || cyoGroup.numberofUnitsRequiredForComponent;
    const qtySelected = parseInt(selectedItems?.[component.legacyId], 10) || 0;

    // regex will replace every alphabetic or numberic character before ":" with empty string ''
    // thus replacing http or https from image url
    const itemImagePath = component.image?.path?.replace(/(^\w+:|^)/, '');
    const itemImageName = component.image?.name;
    return (
        <div key={seed(component)}>
            <Grid container>
                <Grid item xs={3} className={Object.keys(selectedData).length === 0 ? classes.cyoImageWrapper : `${classes.cyoImageWrapper} ${classes.cyoImageWrapperSelectedItems}`}>
                    {Object.keys(selectedData).length > 0 && <Typography className={classes.addedText}>Added</Typography>}
                    <img src={`${itemImagePath}${itemImageName}c.jpg`} alt={component.name} className={Object.keys(selectedData).length === 0 ? classes.cyoImage : `${classes.cyoImage} ${classes.cyoImageSelectedItems}`} />
                </Grid>
                <Grid item xs={9}>
                    <Typography className={classes.prodName}>{ReactHtmlParser(component.name)}</Typography>
                    <>
                        {Object.keys(selectedData).length === 0
                            && (
                                <Grid item xs={12} container justify="flex-end">
                                    <>
                                        <RemoveIcon id={`removeIcon${component.legacyId}`} className={classes.addRemoveIcon} onClick={handleSelectedCyoProduct(component, maxSelectables, cyoGroup, true, false)} />
                                        <div className={classes.totalProds}>{qtySelected}</div>
                                        <AddIcon id={`addIcon${component.legacyId}`} className={classes.addRemoveIcon} onClick={handleSelectedCyoProduct(component, maxSelectables, cyoGroup, false, true)} />
                                    </>
                                </Grid>
                            )}
                        {Object.keys(selectedData).length > 0
                            && (
                                <Grid item xs={12} container>
                                    <Grid item xs={6} container justify="flex-start">
                                        <Button variant="outlined" size="small" id={`removeBtn${component.legacyId}`} className={classes.removebtn} onClick={() => handleRemoveProduct(selectedData.productId, true)}>Remove</Button>
                                    </Grid>
                                    {selectedData.productId !== selectedBand
                                        && (
                                            <Grid item xs={6} container justify="flex-end">
                                                <>
                                                    <RemoveIcon id={`selectedRemoveIcon${component.legacyId}`} className={classes.addRemoveIcon} onClick={handleSelectedCyoProduct(component, maxSelectables, cyoGroup, true, false)} />
                                                    <div className={classes.totalProds}>{qtySelected}</div>
                                                    <AddIcon id={`selectedAddIcon${component.legacyId}`} className={classes.addRemoveIcon} onClick={handleSelectedCyoProduct(component, maxSelectables, cyoGroup, false, true)} />
                                                </>
                                            </Grid>
                                        )}
                                </Grid>
                            )}
                    </>
                </Grid>
            </Grid>
            {errorMessage && <div className={classes.errorMessage}>{errorMessage}</div>}
        </div>
    );
};

MobileCYOItems.propTypes = {
    classes: object.isRequired,
    cyoGroup: shape({
        numberofUnitsRequiredForComponent: number.isRequired,
    }).isRequired,
    component: shape({
        id: string.isRequired,
        legacyId: string.isRequired,
        name: string.isRequired,
        image: shape({
            name: string.isRequired,
            path: string.isRequired,
        }),
    }).isRequired,
    maxComponents: number.isRequired,
    handleSelectedCyoProduct: func.isRequired,
    selectedItems: object.isRequired,
    errorMessage: string,
    handleRemoveProduct: func,
    selectedData: object,
    selectedBand: string,
};

MobileCYOItems.defaultProps = {
    errorMessage: '',
    handleRemoveProduct: () => {},
    selectedData: {},
    selectedBand: '',
};

export default withStyles(styles)(MobileCYOItems);
